import React from "react";
import { getTimeValue } from "../../store/helper";
import useTheme from "../../../../common/hooks/useTheme";

type Props = {
  label: string;
  value: number;
  maxValue?: number;
  weeklyValue?: number;
  unit?: string;
  hasBoldLabel?: boolean;
  showPercentage?: boolean;
  isDisabled?: boolean;
  showStatisticsIcon?: boolean;
  dataType?: string;
  chartType?: number;
  tab?: string;
};

const BarInner = ({ val, maxVal }: { val: number; maxVal: number }) => {
  const theme = useTheme();

  let barInnerWidth, barInnerType;

  if ((val * 100) / maxVal > 100) {
    barInnerWidth = "100%";
    barInnerType = "isOverTarget";
  } else if ((val * 100) / maxVal <= 100 && (val * 100) / maxVal > 50) {
    barInnerWidth = `${(val * 100) / maxVal}%`;
    barInnerType = "isAboveAverage";
  } else {
    barInnerWidth = `${(val * 100) / maxVal}%`;
    barInnerType = "lowValue";
  }

  return <div style={styles.barInner(theme, barInnerWidth, barInnerType)} />;
};

const ItemCardHome = ({
  label,
  value,
  maxValue,
  weeklyValue,
  unit = "",
  hasBoldLabel = false,
  showPercentage,
  isDisabled = false,
  showStatisticsIcon,
  dataType,
  chartType,
  tab,
}: Props) => {
  const theme = useTheme();

  return (
    <div style={styles.container(theme, isDisabled)}>
      <div style={styles.label(theme, hasBoldLabel)}>{label}</div>
      {maxValue !== undefined && maxValue !== 0 && (
        <div style={styles.barContainer(theme, isDisabled)}>
          <BarInner val={value} maxVal={Math.round(maxValue)} />
        </div>
      )}
      <div style={styles.valuesContainer}>
        {showPercentage ? (
          <span style={styles.valueText(theme)}>{`${value}%`}</span>
        ) : (
          <div>
            <span style={styles.valueText(theme)}>
              {label === "Sleep time"
                ? getTimeValue(value)
                : unit === "L"
                ? `${value} `
                : `${Math.round(value)} `}
            </span>
            {maxValue !== undefined ? (
              <span style={styles.maxValueText(theme)}>
                {label === "Sleep time"
                  ? ` / ${getTimeValue(maxValue)}`
                  : ` / ${Math.round(maxValue)} ${unit}`}
              </span>
            ) : (
              <span style={styles.maxValueText(theme)}>{unit}</span>
            )}
          </div>
        )}
        {weeklyValue ? (
          <div style={styles.weeklyAvgContainer}>
            {Math.round(value) !== weeklyValue && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{
                  transform:
                    Math.round(value) > weeklyValue ? "rotate(180deg)" : "",
                }}
              >
                <path
                  d="M11.9426 5.269C11.9004 5.18796 11.8367 5.12005 11.7586 5.07262C11.6805 5.0252 11.5909 5.00008 11.4996 5H4.49957C4.40812 5.00008 4.31843 5.02524 4.24028 5.07274C4.16213 5.12025 4.09851 5.18828 4.05634 5.26943C4.01416 5.35058 3.99505 5.44175 4.00109 5.533C4.00713 5.62426 4.03808 5.71211 4.09057 5.787L7.59057 10.787C7.63671 10.8526 7.69796 10.9062 7.76914 10.9431C7.84033 10.9801 7.91936 10.9994 7.99957 10.9994C8.07978 10.9994 8.15882 10.9801 8.23 10.9431C8.30119 10.9062 8.36243 10.8526 8.40857 10.787L11.9086 5.787C11.9611 5.71204 11.9921 5.6241 11.9981 5.53276C12.0041 5.44142 11.9849 5.35018 11.9426 5.269Z"
                  fill="#6D7075"
                />
              </svg>
            )}
            <span style={styles.weeklyAvgText(theme)}>{weeklyValue}</span>
          </div>
        ) : (
          <span style={styles.weeklyAvgText(theme)}>{weeklyValue}</span>
        )}
      </div>
    </div>
  );
};

export default ItemCardHome;

const styles = {
  container: (theme: any, isDisabled: boolean) => ({
    width: "100%",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "space-between",
    textAlign: "left" as "left",
    backgroundColor: isDisabled
      ? theme.colorTokens.N[30]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  label: (theme: any, hasBoldLabel: boolean) => ({
    fontFamily: hasBoldLabel ? "var(--font-Bold)" : "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  barContainer: (theme: any, isDisabled: boolean) => ({
    flex: 1,
    height: "6px",
    margin: "4px 0",
    borderRadius: "38px",
    overflow: "hidden",
    backgroundColor: isDisabled
      ? theme.colorTokens.N[40]
      : theme.colorTokens.P[10],
  }),
  barInner: (theme: any, customWidth: string, type: string) => ({
    width: customWidth,
    height: "6px",
    borderRadius: "38px",
    background:
      type === "isOverTarget"
        ? `linear-gradient(to right, ${theme.colorTokens.P[60]}, ${theme.colorTokens.P[90]}, ${theme.colorTokens.D[60]})`
        : type === "isAboveAverage"
        ? `linear-gradient(to right, ${theme.colorTokens.P[60]}, ${theme.colorTokens.P[90]})`
        : theme.colorTokens.P[60],
  }),
  valueText: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    lineHeight: "14px",
  }),
  maxValueText: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    lineHeight: "14px",
  }),
  valuesContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  weeklyAvgContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  weeklyAvgText: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "14px",
  }),
};
